import { useLogger } from '~/composables/useLogger'
const logger = useLogger('FavoritesSyncPlugin')

export default defineNuxtPlugin(async () => {
  const { identity } = useIdentity()
  const { syncGuestFavoritesToDB, resetGuestFavorites } = useFavorites()
  const { generateGuestToken } = useGuestToken()
  const userStore = useUserStore()

  // variable to store the timeout and be able to cancel it
  let syncTimeout: ReturnType<typeof setTimeout> | null = null

  // Observe authentication state changes
  const unwatch = watch(
    () => identity.value,
    async (newUser, oldUser) => {
      // Clear timeout if it exists
      if (syncTimeout) {
        clearTimeout(syncTimeout)
        syncTimeout = null
      }

      // Login/Register: Synchronize only when the user transitions from unauthenticated to authenticated
      if (oldUser == null && newUser != null) {
        // Small delay to ensure the session is fully established
        syncTimeout = setTimeout(() => {
          void (async () => {
            // Fetch user access to get capabilities
            await userStore.fetchUserAccess()

            // Only sync favorites if user has consumer access
            if (userStore.hasConsumerAccess) {
              await syncGuestFavoritesToDB()
            }

            syncTimeout = null
          })().catch((error: unknown) => {
            logger.error('syncFavorites', { error })
            syncTimeout = null
          })
        }, 500)
      }

      // Logout: regenerate guest token and reset favorites
      if (oldUser != null && newUser == null) {
        void generateGuestToken()
        resetGuestFavorites()
      }
    },
    { immediate: false }
  )

  // Cleanup function when the app is unmounted
  return {
    provide: {
      cleanupFavoritesSync: () => {
        if (syncTimeout) {
          clearTimeout(syncTimeout)
          syncTimeout = null
        }
        unwatch()
      }
    }
  }
})
